import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Send, Users, Mail, Loader2, Info, Copy, Check, 
  UserPlus, BarChart3, Clock, RefreshCw, Edit3
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import api from '../services/api';

const InviteUsers = () => {
  const { user } = useAuth();
  const { company, isSubdomain } = useSubdomain();
  // Short invite link (/register/<code>). Fetched from the backend, which owns
  // the code; null until it loads, so the long form is used until then.
  const [shortInvite, setShortInvite] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/selling/my-link')
      .then((r) => { if (!cancelled) setShortInvite(r.data?.invite_url || null); })
      .catch(() => { if (!cancelled) setShortInvite(null); });
    return () => { cancelled = true; };
  }, []);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [emails, setEmails] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [template, setTemplate] = useState('');
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const isAdmin = user?.role && ['admin', 'main_admin', 'company_admin'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templateRes, statsRes] = await Promise.all([
        api.get('/invites/template'),
        api.get('/invites/stats')
      ]);
      
      setTemplate(templateRes.data.template_message);
      setDefaultTemplate(templateRes.data.default_template);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch invite data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate the referral link
  const getReferralLink = () => {
    const userId = user?.id;
    const companyId = user?.company_id;
    
    // Default to www.trados.app for main admin (no company)
    let baseUrl = 'https://www.trados.app';
    
    // For company users, use their subdomain
    if (isSubdomain && company?.subdomain) {
      baseUrl = `https://${company.subdomain}.trados.app`;
    } else if (companyId && company?.subdomain) {
      baseUrl = `https://${company.subdomain}.trados.app`;
    }
    
    // Prefer the short form (/register/7f3k2q). The backend owns the code
    // because it lives on the user record; the long ?ref_user=&ref_company=
    // URL below stays as the fallback and still works everywhere.
    if (shortInvite) return shortInvite;

    let link = `${baseUrl}/register`;
    const params = [];

    if (userId) {
      params.push(`ref_user=${userId}`);
    }
    if (companyId) {
      params.push(`ref_company=${companyId}`);
    }

    if (params.length > 0) {
      link += '?' + params.join('&');
    }

    return link;
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Referral link copied!');
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleSendInvites = async () => {
    // Parse emails - split by comma, newline, or semicolon
    const emailList = emails
      .split(/[,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    
    if (emailList.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }
    
    if (emailList.length > 100) {
      toast.error('Maximum 100 emails allowed per request');
      return;
    }

    setSending(true);
    try {
      const res = await api.post('/invites/send', {
        emails: emailList,
        custom_message: useCustomMessage ? customMessage : null
      });
      
      if (res.data.sent_count > 0) {
        toast.success(`${res.data.sent_count} invitation(s) sent successfully!`);
      }
      
      // Show specific error reasons for failed emails
      if (res.data.failed_emails && res.data.failed_emails.length > 0) {
        res.data.failed_emails.forEach(failed => {
          toast.error(`${failed.email}: ${failed.reason}`);
        });
      }
      
      setEmails('');
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await api.put('/invites/template', {
        template_message: template
      });
      toast.success('Email template saved!');
      setEditingTemplate(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    if (!window.confirm('Reset email template to default?')) return;
    
    try {
      const res = await api.post('/invites/template/reset');
      setTemplate(res.data.template_message);
      toast.success('Template reset to default');
      setEditingTemplate(false);
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Invite Users</h1>
          <p className="text-gray-500 mt-1">Invite clients, partners, and potential customers to join the platform</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Invite Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referral Link Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Your Referral Link</h2>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Share this link directly. Anyone who signs up through it will be tracked as your referral.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={getReferralLink()}
                  className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-mono text-gray-700"
                />
                <button
                  onClick={copyReferralLink}
                  className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
                  data-testid="copy-referral-link"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Send Invites Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Send className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Send Invitations</h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Addresses <span className="text-gray-400">(max 100, separated by commas or new lines)</span>
                  </label>
                  <textarea
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com&#10;email3@example.com"
                    className="w-full h-32 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm resize-none"
                    data-testid="invite-emails-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {emails.split(/[,;\n]+/).filter(e => e.trim() && e.includes('@')).length} email(s) detected
                  </p>
                </div>

                {/* Custom Message Toggle */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setUseCustomMessage(!useCustomMessage)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      useCustomMessage ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span 
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        useCustomMessage ? 'translate-x-6' : ''
                      }`}
                    />
                  </button>
                  <span className="text-sm text-gray-700">Use custom message for this batch</span>
                </div>

                {useCustomMessage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Message
                    </label>
                    <textarea
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter your custom message... Use {signup_link}, {user_name}, {company_name} as placeholders"
                      className="w-full h-40 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm resize-none"
                    />
                  </div>
                )}

                <button
                  onClick={handleSendInvites}
                  disabled={sending || !emails.trim()}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="send-invites-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending Invitations...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Invitations
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Email Template (Admin Only) */}
            {isAdmin && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Email Template</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingTemplate ? (
                      <>
                        <button
                          onClick={handleResetTemplate}
                          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                        >
                          Reset
                        </button>
                        <button
                          onClick={handleSaveTemplate}
                          disabled={savingTemplate}
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                        >
                          {savingTemplate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditingTemplate(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:text-purple-800"
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 mb-3">
                  Customize the default email message sent to invitees. Available placeholders:
                  <code className="mx-1 px-1 bg-gray-100 rounded text-xs">{'{signup_link}'}</code>
                  <code className="mx-1 px-1 bg-gray-100 rounded text-xs">{'{user_name}'}</code>
                  <code className="mx-1 px-1 bg-gray-100 rounded text-xs">{'{company_name}'}</code>
                </p>
                
                <textarea
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  disabled={!editingTemplate}
                  className={`w-full h-48 px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none ${
                    editingTemplate 
                      ? 'focus:outline-none focus:ring-2 focus:ring-purple-500' 
                      : 'bg-gray-50 text-gray-600'
                  }`}
                />
                
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <strong>Note:</strong> The referral link (<code>{'{signup_link}'}</code>) cannot be modified and will always include your tracking ID.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Stats Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Your Stats</h2>
                </div>
                <button onClick={fetchData} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <RefreshCw className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <span className="text-sm text-blue-700">Invites Sent</span>
                  <span className="text-xl font-bold text-blue-800">{stats?.total_invites_sent || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                  <span className="text-sm text-green-700">Signed Up</span>
                  <span className="text-xl font-bold text-green-800">{stats?.total_referred || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                  <span className="text-sm text-purple-700">Conversion Rate</span>
                  <span className="text-xl font-bold text-purple-800">
                    {stats?.total_invites_sent > 0 
                      ? Math.round((stats?.total_referred / stats?.total_invites_sent) * 100) 
                      : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* Recent Referrals */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Recent Signups</h2>
                </div>
                <button onClick={fetchData} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <RefreshCw className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              {stats?.referred_users?.length > 0 ? (
                <div className="space-y-3">
                  {stats.referred_users.slice(0, 5).map((referral, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{referral.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{referral.email}</p>
                      </div>
                      {referral.verified && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Verified</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No referrals yet. Share your link to get started!
                </p>
              )}
            </div>

            {/* Recent Invites */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Recent Invites</h2>
                </div>
                <button onClick={fetchData} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <RefreshCw className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              
              {stats?.invites_sent?.length > 0 ? (
                <div className="space-y-3">
                  {stats.invites_sent.slice(0, 5).map((invite, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <p className="text-sm text-gray-700 truncate max-w-[150px]">{invite.email}</p>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        invite.status === 'registered' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {invite.status === 'registered' ? 'Signed Up' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No invites sent yet
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default InviteUsers;
